/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import { useMemo, type FC } from 'react'
import { Button, Col, Form, Row, Stack } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

const validationSchema = yup.object().shape({
  description: yup.string().required('O nome é obrigatório').default('').min(0).max(255),
  status: yup.string().oneOf(['active', 'inactive', 'deleted']).default('inactive'),
  url: yup
    .string()
    .default('')
    .matches(
      /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/,
      'O link do vídeo deve ser um link válido do YouTube'
    )
    .min(0)
    .max(255),
  publishedIn: yup.string().default(() => moment().format('YYYY-MM-DDTHH:mm')),
  unpublishedIn: yup.string().default(() => moment().add(5, 'days').format('YYYY-MM-DDTHH:mm')),
  updatedAt: yup.string(),
  createdAt: yup.string()
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormVideoHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormVideoHandleSubmit,
  initialValues?: Partial<ValidationSchema>,
  isLoading?: boolean
}

/**
 * FormVideo is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormVideo from './src/views/PostEdit/FormVideo.tsx'
 *
 * const MyComponent = () => (
 *   <FormVideo />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormVideo: FC<Props> = ({
  onSubmit = () => {},
  initialValues = {},
  isLoading = false,
}) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Row>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Descrição</Form.Label>
          <Form.Control
            size="sm"
            type='text'
            placeholder='Informe uma descrição para o vídeo'
            name='description'
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
          <Form.Text id="passwordHelpBlock" muted>
            A descrição deve ter no máximo 255 caracteres e é utilizada para identificar o vídeo.
          </Form.Text>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Situação</Form.Label>
          <Form.Select
            size="sm"
            value={formik.values.status}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
            name="status"
          >
            <option>Selecione uma opção</option>
            <option value='active'>🌎 Pública</option>
            <option value='inactive'>🔒 Privada</option>
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Link do vídeo</Form.Label>
          <Form.Control
            size="sm"
            type='text'
            placeholder='Informe o link do vídeo no YouTube'
            name='url'
            value={formik.values?.url}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
          <Form.Text id="passwordHelpBlock" muted>
            O link do vídeo deve ter no máximo 255 caracteres, nele você deve informar o endereço do vídeo no YouTube
            {' '}
            no formato https://www.youtube.com/watch?v=VIDEO_ID
          </Form.Text>
          {formik.touched.url && formik.errors.url ? (
            <Form.Text className="text-danger">
              {formik.errors.url}
            </Form.Text>
          ) : null}
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <Form.Label>Data de início</Form.Label>
          <Form.Control
            size="sm"
            type='datetime-local'
            name='publishedIn'
            value={formik?.values?.publishedIn ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
          <Form.Text muted>
            A data de início é a data em que o vídeo será exibido no site, caso não seja informada o vídeo será exibido imediatamente.
          </Form.Text>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <Form.Label>Data de término</Form.Label>
          <Form.Control
            size="sm"
            type='datetime-local'
            name='unpublishedIn'
            value={formik?.values?.unpublishedIn ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
          <Form.Text muted>
            A data de término é a data em que o vídeo deixará de ser exibido no site, caso não seja informada o vídeo será exibido indefinidamente.
          </Form.Text>
        </Form.Group>
        <Col md={12}>
          <Stack direction='horizontal' gap={2}>
            <div className="p-2">
              <Button
                variant="link"
                style={{ marginRight: 10 }} onClick={() => navigate('/videos')}
                disabled={formik.isSubmitting || isLoading}
              >
                Voltar
              </Button>
              <Button
                type="submit"
                disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
              >
                Salvar
              </Button>
            </div>
            <div className="p-2 ms-auto">
              <small>
                <small>
                  Modificado em:
                  {' '}
                  {moment(formik?.values?.updatedAt ?? formik?.values?.createdAt).format('LLL')}
                </small>
              </small>
            </div>
          </Stack>
        </Col>
      </Row>
    </Form>
  )
}

export default FormVideo
